import { useEffect, useMemo, useState } from "react";
import { GrayBase, geomPath, useCounties, W, H } from "@/components/broadcast/TvMap";
import { API } from "@/lib/api";

// -------------------- Alert grouping (one graphic per event type) --------------------
const STATE_SUFFIX = /,\s*([A-Z]{2})$/;
const AREA_PREFIX = /^(north|south|east|west|central|inland|coastal|northern|southern|eastern|western|upper|lower|northeast|northwest|southeast|southwest|north central|south central|east central|west central)\s+/i;
export const normCounty = (s) => s.replace(AREA_PREFIX, "").replace(/\s+county$/i, "").trim();

export function splitAreas(area) {
  return (area || "").split(";").map((t) => t.trim()).filter(Boolean).flatMap((t) => {
    const m = t.match(STATE_SUFFIX);
    if (m && m[1] !== "GA") return [];
    return [normCounty(t.replace(STATE_SUFFIX, ""))];
  });
}

const HAZARD_PRIORITY = [/tornado/, /hurricane|tropical|storm surge/, /severe thunderstorm|extreme wind/, /flash flood/, /flood/, /winter|ice|blizzard|snow|freeze|frost/, /wind/, /heat/];
const rank = (title = "") => {
  const t = title.toLowerCase();
  const cls = /tornado warning|extreme wind/.test(t) ? 0 : /warning/.test(t) ? 1 : /watch/.test(t) ? 2 : /advisory/.test(t) ? 3 : 4;
  const pri = HAZARD_PRIORITY.findIndex((re) => re.test(t));
  return cls * 10 + (pri < 0 ? 9 : pri);
};

export function groupAlerts(alerts = []) {
  const map = new Map();
  for (const a of alerts) {
    const key = `${a.source || "NWS"}|${a.title || ""}`;
    let g = map.get(key);
    if (!g) {
      g = { id: key, title: a.title || "Alert", source: a.source || "NWS", severity: a.severity, headline: a.headline, expires: null, counties: [], geometries: [], ids: [] };
      map.set(key, g);
    }
    g.ids.push(a.id);
    if (a.geometry?.coordinates) g.geometries.push(a.geometry);
    for (const c of splitAreas(a.area)) if (!g.counties.includes(c)) g.counties.push(c);
    if (a.expires && (!g.expires || new Date(a.expires) > new Date(g.expires))) g.expires = a.expires;
  }
  return [...map.values()].sort((x, y) => rank(x.title) - rank(y.title) || y.counties.length - x.counties.length);
}

// NWS hazard colours (official map palette), toned for a light basemap
const NWS_COLORS = [
  [/tornado warning/, "#ff0000"], [/extreme wind/, "#ff8c00"], [/severe thunderstorm warning/, "#ffa500"],
  [/flash flood warning/, "#8b0000"], [/flood warning/, "#00a86b"], [/flood advisory/, "#3cb371"], [/flood watch/, "#2e8b57"],
  [/hurricane warning/, "#dc143c"], [/hurricane watch/, "#ff00ff"], [/tropical storm warning/, "#b22222"], [/tropical storm watch/, "#f08080"],
  [/storm surge warning/, "#b524f7"], [/storm surge watch/, "#db7ff7"], [/tropical cyclone local statement/, "#ffe4b5"],
  [/tornado watch/, "#ffd700"], [/severe thunderstorm watch/, "#db7093"],
  [/winter storm warning/, "#ff69b4"], [/winter storm watch/, "#4682b4"], [/winter weather advisory/, "#7b68ee"], [/ice storm/, "#8b008b"], [/blizzard/, "#ff4500"],
  [/freeze warning/, "#483d8b"], [/freeze watch/, "#00ffff"], [/frost/, "#6495ed"], [/wind chill|cold weather/, "#b0c4de"],
  [/high wind warning/, "#daa520"], [/high wind watch/, "#b8860b"], [/wind advisory/, "#d2b48c"],
  [/excessive heat|extreme heat/, "#c71585"], [/heat advisory/, "#ff7f50"],
  [/dense fog/, "#708090"], [/red flag/, "#ff1493"], [/fire weather/, "#ffdead"], [/air quality/, "#808080"],
  [/rip current/, "#40e0d0"], [/high surf/, "#228b22"], [/coastal flood/, "#6b8e23"], [/small craft/, "#d8bfd8"], [/gale/, "#dda0dd"],
  [/special weather statement/, "#ffe4b5"],
];
export function hazardColor(title = "") {
  const t = title.toLowerCase();
  for (const [re, c] of NWS_COLORS) if (re.test(t)) return c;
  if (/warning/.test(t)) return "#d90429";
  if (/watch/.test(t)) return "#f6a21c";
  if (/advisory/.test(t)) return "#eab308";
  return "#9ca3af";
}
const isLightColor = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return (r * 299 + g * 587 + b * 114) / 1000 > 150;
};
// Storm-based warnings are drawn as their polygon rather than whole counties
const STORM_BASED = /tornado warning|severe thunderstorm warning|flash flood warning|extreme wind warning|special weather statement|flood advisory|special marine/i;

// -------------------- Mercator helpers for the zoomable inset --------------------
const R = 6378137;
const mx = (lon) => (lon * Math.PI / 180) * R;
const my = (lat) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360)) * R;
const INSET = 1000;
const MIN_SPAN_M = 300000; // ≈ 2.7° of longitude — keeps single-county zooms readable with context
const GA_EXTENT = { w: -85.7, s: 30.3, e: -80.75, n: 35.05 };

const REF_CITIES = [
  ["Atlanta", 33.749, -84.388, 1], ["Macon", 32.84, -83.63, 1], ["Columbus", 32.46, -84.99, 1], ["Augusta", 33.47, -81.97, 1], ["Savannah", 32.08, -81.09, 1],
  ["Albany", 31.58, -84.16, 1], ["Valdosta", 30.83, -83.28, 1], ["Athens", 33.95, -83.38, 1], ["Rome", 34.26, -85.16, 1], ["Brunswick", 31.15, -81.49, 1],
  ["Dalton", 34.77, -84.97, 2], ["Waycross", 31.21, -82.35, 2], ["Thomasville", 30.84, -83.98, 2], ["Statesboro", 32.45, -81.78, 2], ["Dublin", 32.54, -82.9, 2],
  ["Gainesville", 34.3, -83.82, 2], ["Tifton", 31.45, -83.51, 2], ["Americus", 32.07, -84.23, 2], ["Bainbridge", 30.9, -84.58, 2], ["LaGrange", 33.04, -85.03, 2],
];

export function gaOnly(names, counties) {
  if (!counties.length) return names;
  const set = new Set(counties.map((c) => (c.name || "").toLowerCase()));
  return names.filter((n) => set.has(n.toLowerCase()));
}

function groupView(group, counties) {
  const names = new Set(group.counties.map((c) => c.toLowerCase()));
  const hits = counties.filter((c) => names.has((c.name || "").toLowerCase()));
  let w = 180, e = -180, s = 90, n = -90;
  const add = (lon, lat) => { w = Math.min(w, lon); e = Math.max(e, lon); s = Math.min(s, lat); n = Math.max(n, lat); };
  hits.forEach((c) => { add(c.bbox.w, c.bbox.s); add(c.bbox.e, c.bbox.n); });
  if (STORM_BASED.test(group.title)) group.geometries.forEach((g) => geomRings(g).forEach((ring) => ring.forEach(([lon, lat]) => add(lon, lat))));
  if (w > e) ({ w, s, e, n } = GA_EXTENT);
  let x0 = mx(w), x1 = mx(e), y0 = my(s), y1 = my(n);
  const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
  const span = Math.max((x1 - x0) * 1.3, (y1 - y0) * 1.3, MIN_SPAN_M);
  x0 = cx - span / 2; x1 = cx + span / 2; y0 = cy - span / 2; y1 = cy + span / 2;
  const proj = (lat, lon) => ({ x: ((mx(lon) - x0) / span) * INSET, y: ((y1 - my(lat)) / span) * INSET });
  const key = [x0, y0, x1, y1].map((v) => Math.round(v)).join(",");
  const esri = (svc) => `https://server.arcgisonline.com/ArcGIS/rest/services/${svc}/MapServer/export?bbox=${key}&bboxSR=3857&size=${INSET},${INSET}&imageSR=3857&format=png32&transparent=true&f=image`;
  return { hits, proj, key, span, base: esri("Canvas/World_Light_Gray_Base"), relief: esri("Elevation/World_Hillshade"), roads: esri("Reference/World_Transportation") };
}

const geomRings = (g) => !g ? [] : g.type === "Polygon" ? [g.coordinates[0]] : g.coordinates.map((p) => p[0]);
const ringToPath = (ring, proj) => ring.map(([lon, lat], i) => { const p = proj(lat, lon); return `${i ? "L" : "M"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`; }).join(" ") + "Z";
const countyPath = (c, proj) => c.rings.map((r) => ringToPath(r, proj)).join(" ");

export function prefetchAlertMaps(groups, counties) {
  if (typeof Image === "undefined" || !counties.length) return;
  groups.forEach((g) => { const v = groupView(g, counties); [v.base, v.relief, v.roads].forEach((u) => { const im = new Image(); im.src = u; }); });
}

// Zoomed, broadcast-styled inset: light basemap + relief + roads, affected counties filled in the NWS hazard colour.
export function AlertAreaMap({ group, className = "" }) {
  const counties = useCounties();
  const view = useMemo(() => groupView(group, counties), [group, counties]);
  const color = hazardColor(group.title);
  const light = isLightColor(color);
  const storm = STORM_BASED.test(group.title);
  const { proj, hits } = view;
  const labelAll = hits.length <= 14;
  const outsideMask = counties.length ? `M0 0H${INSET}V${INSET}H0Z ` + counties.map((c) => countyPath(c, proj)).join(" ") : "";
  return (
    <svg viewBox={`0 0 ${INSET} ${INSET}`} className={className} preserveAspectRatio="xMidYMid slice" data-testid="alert-area-map">
      <image href={view.base} x="0" y="0" width={INSET} height={INSET} preserveAspectRatio="none" style={{ filter: "saturate(5) hue-rotate(-18deg) brightness(0.86) contrast(1.15)" }} />
      <image href={view.relief} x="0" y="0" width={INSET} height={INSET} preserveAspectRatio="none" opacity="0.9" style={{ mixBlendMode: "multiply", filter: "contrast(1.3)" }} />
      <rect x="0" y="0" width={INSET} height={INSET} fill="#5c6670" style={{ mixBlendMode: "multiply", opacity: 0.35 }} />
      {!storm && hits.map((c) => (
        <path key={c.name} d={countyPath(c, proj)} fill={color} fillOpacity="0.78" stroke="#fff" strokeWidth="3" strokeLinejoin="round" data-testid="alert-county-fill" />
      ))}
      {storm && group.geometries.map((g, i) => (
        <path key={i} d={geomRings(g).map((r) => ringToPath(r, proj)).join(" ")} fill={color} fillOpacity="0.62" stroke="#fff" strokeWidth="4" strokeLinejoin="round" data-testid="alert-polygon-shape" />
      ))}
      <image href={view.roads} x="0" y="0" width={INSET} height={INSET} preserveAspectRatio="none" opacity="0.9" />
      <g fill="none" stroke="#1c2430" strokeWidth="1.6" strokeLinejoin="round" opacity="0.85">
        {counties.map((c) => <path key={c.name} d={countyPath(c, proj)} />)}
      </g>
      {outsideMask && <path d={outsideMask} fill="#dfe3e8" fillOpacity="0.55" fillRule="evenodd" />}
      {!storm && hits.map((c) => <path key={`o-${c.name}`} d={countyPath(c, proj)} fill="none" stroke="#fff" strokeWidth="3.5" strokeLinejoin="round" />)}
      {REF_CITIES.map(([name, lat, lon, tier]) => {
        if (tier > 1 && view.span > 420000) return null;
        const p = proj(lat, lon);
        if (p.x < 40 || p.x > INSET - 40 || p.y < 40 || p.y > INSET - 40) return null;
        return (
          <g key={name}>
            <circle cx={p.x} cy={p.y} r="7" fill="#0a0f1c" stroke="#fff" strokeWidth="3" />
            <text x={p.x + 14} y={p.y + 8} fontFamily="Oswald, sans-serif" fontWeight="700" fontSize="24" fill="#0a0f1c" stroke="#fff" strokeWidth="5" paintOrder="stroke" letterSpacing="1" style={{ textTransform: "uppercase" }}>{name}</text>
          </g>
        );
      })}
      {!storm && labelAll && hits.map((c) => {
        const p = proj(c.centroid[1], c.centroid[0]);
        return <text key={`l-${c.name}`} x={p.x} y={p.y + 9} textAnchor="middle" fontFamily="Oswald, sans-serif" fontWeight="800" fontSize={hits.length > 6 ? 24 : 30} fill={light ? "#0a0f1c" : "#fff"} stroke={light ? "#fff" : "#0a0f1c"} strokeWidth="5" paintOrder="stroke" letterSpacing="1" style={{ textTransform: "uppercase" }}>{c.name}</text>;
      })}
    </svg>
  );
}

// -------------------- Full-bleed statewide alerts map --------------------
export function AlertsLegend({ groups = [] }) {
  const show = groups.slice(0, 6);
  if (!show.length) return null;
  return (
    <div className="flex items-center gap-4 font-oswald text-[20px] font-bold uppercase tracking-wider text-white" data-testid="alerts-legend">
      {show.map((g) => (
        <span key={g.id} className="flex items-center gap-2"><span className="h-5 w-7 rounded-sm border-2 border-white/80" style={{ background: hazardColor(g.title) }} />{g.title}</span>
      ))}
      {groups.length > 6 && <span className="opacity-70">+{groups.length - 6} more</span>}
    </div>
  );
}

export function TvAlertsMap({ groups = [] }) {
  const counties = useCounties();
  const byName = useMemo(() => new Map(counties.map((c) => [(c.name || "").toLowerCase(), c])), [counties]);
  const layers = [...groups].reverse(); // draw lowest rank first so warnings sit on top
  const totalCounties = gaOnly([...new Set(groups.flatMap((g) => g.counties))], counties).length;
  useEffect(() => { prefetchAlertMaps(groups, counties); }, [groups, counties]);
  return (
    <div className="relative h-full w-full overflow-hidden bg-[#cfd3d8]" data-testid="tv-alerts-map">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-full w-full" preserveAspectRatio="xMidYMid slice">
        <GrayBase counties={counties}>
          {layers.map((g) => {
            const color = hazardColor(g.title);
            if (STORM_BASED.test(g.title)) {
              return g.geometries.map((geom, i) => <path key={`${g.id}-${i}`} d={geomPath(geom)} fill={color} fillOpacity="0.65" stroke="#fff" strokeWidth="3" strokeLinejoin="round" data-testid="alert-polygon-shape" />);
            }
            return g.counties.map((n) => {
              const c = byName.get(n.toLowerCase());
              return c ? <path key={`${g.id}-${n}`} d={c.d} fill={color} fillOpacity="0.78" stroke="#fff" strokeWidth="2" strokeLinejoin="round" data-testid="alert-county-fill" /> : null;
            });
          })}
        </GrayBase>
      </svg>
      <div className="absolute right-[24px] top-[170px] flex items-center gap-4 rounded-[3px] bg-white px-5 py-2 font-oswald text-[30px] font-black uppercase tracking-wide text-[#0a0f1c]" data-testid="alerts-map-chip">
        {groups.length ? <><span>{groups.length} Active Alert{groups.length === 1 ? "" : "s"}</span><span className="rounded-[3px] bg-[#0a0f1c] px-3 py-0.5 text-[24px] text-white">{totalCounties} Counties</span></>
          : <><span className="live-dot" style={{ background: "#16a34a" }} /><span>All Clear · No Active Alerts</span></>}
      </div>
      <div className="absolute bottom-[64px] left-[24px] font-oswald text-[14px] uppercase tracking-widest text-[#0a0f1c]/80">
        National Weather Service active alerts · counties shaded by hazard · storm-based warnings drawn as issued
      </div>
    </div>
  );
}


// -------------------- Single-site storm-relative velocity (warning coverage) --------------------
// Zoomed onto the warning polygon with the nearest NEXRAD's storm-relative velocity overlaid on a
// muted basemap. A tight red (away) / green (toward) couplet = radar-indicated rotation.
export function VelocityAreaMap({ geometry, radar, event = "", className = "" }) {
  const counties = useCounties();
  const [bucket, setBucket] = useState(() => Math.floor(Date.now() / 60000));
  useEffect(() => {
    const t = setInterval(() => setBucket(Math.floor(Date.now() / 60000)), 60000);
    return () => clearInterval(t);
  }, []);
  const group = useMemo(() => ({ title: event || "tornado warning", counties: [], geometries: geometry ? [geometry] : [] }), [event, geometry]);
  const view = useMemo(() => groupView(group, counties), [group, counties]);
  if (!geometry || !radar) return null;
  const { proj } = view;
  const velUrl = `${API}/radar/velocity-frame?site=${radar.id}&bbox=${view.key}&crs=EPSG:3857&width=${INSET}&height=${INSET}&t=${bucket}`;
  const rp = proj(radar.lat, radar.lon);
  const radarInView = rp.x > 6 && rp.x < INSET - 6 && rp.y > 6 && rp.y < INSET - 6;
  return (
    <svg viewBox={`0 0 ${INSET} ${INSET}`} className={className} preserveAspectRatio="xMidYMid slice" data-testid="velocity-area-map">
      <image href={view.base} x="0" y="0" width={INSET} height={INSET} preserveAspectRatio="none" style={{ filter: "grayscale(1) brightness(0.5) contrast(1.1)" }} />
      <rect x="0" y="0" width={INSET} height={INSET} fill="#0a0f1c" opacity="0.35" />
      <image href={velUrl} x="0" y="0" width={INSET} height={INSET} preserveAspectRatio="none" opacity="0.92" data-testid="velocity-overlay" />
      <g fill="none" stroke="#eef2f7" strokeWidth="1.4" strokeLinejoin="round" opacity="0.5">
        {counties.map((c) => <path key={c.name} d={countyPath(c, proj)} />)}
      </g>
      {group.geometries.map((g, i) => (
        <path key={i} d={geomRings(g).map((r) => ringToPath(r, proj)).join(" ")} fill="none" stroke="#ffffff" strokeWidth="6" strokeLinejoin="round" data-testid="velocity-warning-polygon" />
      ))}
      {group.geometries.map((g, i) => (
        <path key={`r-${i}`} d={geomRings(g).map((r) => ringToPath(r, proj)).join(" ")} fill="none" stroke="#d90429" strokeWidth="2.5" strokeLinejoin="round" />
      ))}
      {radarInView && (
        <g data-testid="velocity-radar-site">
          <circle cx={rp.x} cy={rp.y} r="11" fill="none" stroke="#ffd60a" strokeWidth="3" />
          <circle cx={rp.x} cy={rp.y} r="3.5" fill="#ffd60a" />
          <text x={rp.x + 16} y={rp.y + 8} fontFamily="Oswald, sans-serif" fontWeight="800" fontSize="24" fill="#ffd60a" stroke="#0a0f1c" strokeWidth="5" paintOrder="stroke" letterSpacing="1">{radar.id}</text>
        </g>
      )}
      {REF_CITIES.map(([name, lat, lon, tier]) => {
        if (tier > 1 && view.span > 420000) return null;
        const p = proj(lat, lon);
        if (p.x < 40 || p.x > INSET - 40 || p.y < 40 || p.y > INSET - 40) return null;
        return (
          <g key={name}>
            <circle cx={p.x} cy={p.y} r="6" fill="#0a0f1c" stroke="#fff" strokeWidth="2.5" />
            <text x={p.x + 13} y={p.y + 7} fontFamily="Oswald, sans-serif" fontWeight="700" fontSize="22" fill="#fff" stroke="#0a0f1c" strokeWidth="5" paintOrder="stroke" letterSpacing="1" style={{ textTransform: "uppercase" }}>{name}</text>
          </g>
        );
      })}
    </svg>
  );
}
